import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertCircle, ClipboardList } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SurveyQuestion {
  id: string;
  question: string;
  type: "rating" | "text";
  required: boolean;
}

interface SurveySettings {
  id: string;
  is_active: boolean;
  title: string;
  description: string | null;
  questions: SurveyQuestion[];
}

const SurveyPage = () => {
  const [settings, setSettings] = useState<SurveySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [answers, setAnswers] = useState<Record<string, number | string>>({});
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('survey_settings')
        .select('*')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings({
          ...data,
          questions: (data.questions as unknown as SurveyQuestion[]) || []
        });
      }
    } catch (error) {
      console.error('Error loading survey:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (questionId: string, value: number) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setErrors(prev => ({ ...prev, [questionId]: false }));
  };

  const handleTextChange = (questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    if (value.trim().length > 0) {
      setErrors(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const handleSubmit = async () => {
    if (!settings) return;

    // Validate required fields
    const newErrors: Record<string, boolean> = {};
    let hasErrors = false;

    settings.questions.forEach(q => {
      if (q.required) {
        const answer = answers[q.id];
        if (answer === undefined || (typeof answer === 'string' && answer.trim() === '')) {
          newErrors[q.id] = true;
          hasErrors = true;
        }
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      toast.error('Bitte beantworten Sie alle Pflichtfragen');
      return;
    }

    setSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([questionId, value]) => ({
        questionId,
        value
      }));

      const { error } = await supabase
        .from('survey_responses')
        .insert({
          survey_settings_id: settings.id,
          answers: JSON.parse(JSON.stringify(formattedAnswers))
        } as any);

      if (error) throw error;

      setSubmitted(true);
      toast.success('Vielen Dank für Ihre Teilnahme!');
    } catch (error) {
      console.error('Error submitting survey:', error);
      toast.error('Fehler beim Absenden der Umfrage');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Wird geladen...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="pt-6 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Keine aktive Umfrage</h2>
          <p className="text-muted-foreground">
            Derzeit ist keine Mitarbeiterumfrage verfügbar.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (submitted) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardContent className="pt-6 text-center">
          <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-primary" />
          <h2 className="text-2xl font-semibold mb-2">Vielen Dank!</h2>
          <p className="text-muted-foreground">
            Ihre Antworten wurden anonym gespeichert und helfen uns, den Betrieb zu verbessern.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary/10 rounded-lg">
              <ClipboardList className="h-6 w-6 text-primary" />
            </div>
            <div>
              <CardTitle>{settings.title}</CardTitle>
              {settings.description && (
                <CardDescription className="mt-1">{settings.description}</CardDescription>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="secondary">Anonym</Badge>
            <span>Ihre Antworten werden anonym gespeichert</span>
          </div>
        </CardContent>
      </Card>

      {/* Questions */}
      {settings.questions.map((question, index) => (
        <Card key={question.id} className={errors[question.id] ? 'ring-2 ring-destructive' : ''}>
          <CardHeader>
            <div className="flex items-start gap-2">
              <Badge variant="outline" className="mt-0.5">{index + 1}</Badge>
              <div className="flex-1">
                <Label className="text-base font-medium">
                  {question.question}
                  {question.required && <span className="text-destructive ml-1">*</span>}
                </Label>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {question.type === 'rating' ? (
              <div className="space-y-4">
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map(rating => {
                    const isSelected = answers[question.id] === rating;
                    return (
                      <button
                        key={rating}
                        type="button"
                        onClick={() => handleRatingChange(question.id, rating)}
                        className={`
                          flex-1 py-4 rounded-lg border-2 font-bold text-lg transition-all
                          ${isSelected 
                            ? 'bg-primary text-primary-foreground border-primary scale-105 shadow-md' 
                            : 'bg-muted/50 text-muted-foreground border-border hover:border-primary/50 hover:bg-muted'
                          }
                        `}
                      >
                        {rating}
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground px-1">
                  <span>1 = Sehr schlecht</span>
                  <span>5 = Sehr gut</span>
                </div>
              </div>
            ) : (
              <Textarea
                value={(answers[question.id] as string) || ''}
                onChange={(e) => handleTextChange(question.id, e.target.value)}
                placeholder="Ihre Antwort..."
                rows={3}
              />
            )}
            {errors[question.id] && (
              <p className="text-sm text-destructive mt-2">Bitte beantworten Sie diese Frage</p>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Submit */}
      <div className="flex justify-end pb-6">
        <Button onClick={handleSubmit} disabled={submitting} size="lg">
          {submitting ? 'Wird gesendet...' : 'Umfrage absenden'}
        </Button>
      </div>
    </div>
  );
};

export default SurveyPage;
